/**
 * Turning a received outreach into a row on the activity/tasks surfaces — and answering the one
 * question that matters about it: **is this thread waiting on me?**
 *
 * Shared 1:1 with iOS `native-apps/ios/Symphony/Store/OutreachRows.swift` and Android
 * `native-apps/android/app/src/main/java/com/wix/symphony/OutreachRows.kt`. The rules below are a
 * cross-platform contract; change one and change all three.
 *
 * `OutreachStatus` cannot answer that, and assuming it can is the trap this module exists to avoid.
 * In real data a `PENDING` thread had the user's reply already sent while an `IN_PROGRESS` one had
 * been stuck for 69 days on "יונתן צריך תשובה" (*Yonatan needs an answer*). The status tracks the
 * outreach's lifecycle, never whose turn it is.
 *
 * Whose turn it is comes from the LAST message's author, resolved in two ways:
 *
 *  1. `direction` (`FROM_SOURCE` / `FROM_TARGET`), server-set from the writer's own metasite. This is
 *     the real answer and the only one that cannot be fooled.
 *  2. For messages written before that field existed — every historical row — the `"Reply from
 *     <name>: "` prefix the service stamps into the message TEXT.
 *
 * The prefix is a fallback, not a design. It needs the reader's own `Recipient.name` to be exact,
 * because BOTH sides relay through `ReplyToOutreach` and the prefix names whoever replied: on a
 * received outreach `"Reply from <me>"` is mine and `"Reply from <them>"` is theirs. Matching on
 * "does it start with `Reply from`" alone would read a sender's reply as the user's own and quietly
 * mark a waiting thread as handled — the exact failure this is meant to catch.
 */

/** The subset of an outreach message these rules read. `direction` is absent on pre-field rows. */
export type OutreachRowMessage = {
  message?: string;
  createdDate?: string | Date;
  /** 'FROM_SOURCE' | 'FROM_TARGET' | 'UNKNOWN_MESSAGE_DIRECTION' — absent until the field is populated. */
  direction?: string;
};

/** The subset of an outreach these rules read. */
export type OutreachRowSource = {
  id: string;
  status?: string;
  messages?: OutreachRowMessage[];
  sourceRecipientTaskId?: string;
  /** The conversation the outreach was raised FROM — for a worker run, that task's own agent
   *  conversation. The only task link new rows carry; see `sentOutreachTaskId`. */
  sourceConversationId?: string;
  targetMetaSiteId?: string;
  sourceMetaSiteId?: string;
  preferredCommunicationChannel?: string;
  /** The conversation on the RECEIVING side the outreach was delivered into — where the user reads it. */
  conversationId?: string;
  targetConversationId?: string;
  updatedDate?: string | Date;
  createdDate?: string | Date;
  /** Set when this outreach ANNOUNCES A FILE rather than carrying what a person wrote
   *  (`Outreach.shared_file`, written by `ShareFile`'s own notification). Its presence is what makes the
   *  row a share; absent on every ordinary outreach. Structural rather than imported from the generated
   *  client, like every other field here. */
  sharedFile?: { fileId?: string; fileName?: string; shareLink?: string };
};

/** Who the viewer is, for resolving authorship. */
export type OutreachViewer = {
  /** The viewer's Symphony metasite. A received outreach is one whose `targetMetaSiteId` is this. */
  metaSiteId: string;
  /** The viewer's `Recipient.name` — what the service stamps into `"Reply from <name>: "`. */
  name?: string;
};

export type OutreachAuthor = 'me' | 'them' | 'unknown';

const REPLY_PREFIX = 'Reply from ';

/**
 * Who wrote a message on a RECEIVED outreach (one where the viewer is the target).
 *
 * `direction` wins whenever it is populated. Otherwise fall back to the prefix — and only claim "me"
 * on an exact `"Reply from <my name>: "` match. Anything else that carries the prefix names someone
 * who is not the viewer, so it is theirs; anything without a prefix is a source-side push, also theirs.
 * With no name to match on we return `unknown` rather than guess, and the caller decides.
 */
export function authorOfMessage(message: OutreachRowMessage, viewer: OutreachViewer): OutreachAuthor {
  const direction = message.direction;
  if (direction === 'FROM_TARGET') return 'me';
  if (direction === 'FROM_SOURCE') return 'them';

  const text = (message.message ?? '').trimStart();
  if (!text.startsWith(REPLY_PREFIX)) {
    // The opening message and every source follow-up push. Never the viewer's — the receiving side
    // cannot push (`assertCallerIsSource`), only reply.
    return 'them';
  }
  const name = viewer.name?.trim();
  if (!name) return 'unknown';
  return text.startsWith(`${REPLY_PREFIX}${name}:`) ? 'me' : 'them';
}

/** The last message on the thread, or undefined for an empty one. */
export function lastMessage(outreach: OutreachRowSource): OutreachRowMessage | undefined {
  const messages = outreach.messages ?? [];
  return messages.length > 0 ? messages[messages.length - 1] : undefined;
}

/**
 * Is this thread waiting on the viewer? True when it is still open AND the last word was the other
 * side's. An `unknown` author is NOT treated as waiting: a false "needs you" on every historical row
 * would bury the ones that really do.
 */
export function isWaitingOnViewer(outreach: OutreachRowSource, viewer: OutreachViewer): boolean {
  if (outreach.status === 'COMPLETED') return false;
  const last = lastMessage(outreach);
  if (!last) return false;
  return authorOfMessage(last, viewer) === 'them';
}

export type OutreachBucket = 'waiting' | 'inProgress' | 'done';

/**
 * Which bucket the row belongs in — the same three the task list already uses, so an outreach row can
 * sit alongside tasks under the same tabs.
 *
 * `COMPLETED` is done. Otherwise the LAST WORD decides: `waiting` when the other side spoke last, and
 * `done` once the viewer has answered.
 *
 * Answered-is-done is the part worth explaining, because it used to be `inProgress`. The reasoning was
 * that the thread is still live and now waiting on THEM — true of the correspondence, and wrong about
 * the user. Nothing on the receiving side ever moves an outreach to `COMPLETED` on its own: that needs
 * the agent to decide the exchange is finished and call `complete`, which for a courtesy exchange
 * ("here's a photo" / "amazing, thanks") it never will. So a message the user had already answered sat
 * in their task list as live work indefinitely, next to real tasks, with nothing they could do to clear
 * it. Whether the other person writes back again is not the user's open loop, and if they do the new
 * message puts the row straight back into `waiting`.
 *
 * `inProgress` is left for the two cases it is honest about: a thread with no messages at all, and one
 * whose last author cannot be resolved (a pre-`direction` row on a viewer with no name to match the
 * prefix against). Neither is claimed as answered — reporting "done" over an unknown author would hide
 * a message that may well still be waiting, which is the more expensive way to be wrong.
 */
export function bucketOfOutreach(outreach: OutreachRowSource, viewer: OutreachViewer): OutreachBucket {
  if (outreach.status === 'COMPLETED') return 'done';
  const last = lastMessage(outreach);
  if (!last) return 'inProgress';
  const author = authorOfMessage(last, viewer);
  if (author === 'them') return 'waiting';
  return author === 'me' ? 'done' : 'inProgress';
}

/**
 * Is this outreach one the viewer RECEIVED? Rows are authorized for source or target alike, so a
 * query that does not name the target returns the viewer's SENT outreaches too.
 */
export function isReceived(outreach: OutreachRowSource, viewer: OutreachViewer): boolean {
  return !!outreach.targetMetaSiteId && outreach.targetMetaSiteId === viewer.metaSiteId;
}

/**
 * Does this outreach need a row of its OWN, or is it already visible as part of a task?
 *
 * An outreach raised BY one of the viewer's tasks belongs inside that task, not beside it — showing
 * both would report one piece of work twice. An inbound outreach carries a `sourceRecipientTaskId`
 * too, but it names a task in the SENDER's metasite, which can never resolve here; those are the
 * orphans, and they are the ones with nowhere else to appear.
 */
export function isOrphan(outreach: OutreachRowSource, knownTaskIds: ReadonlySet<string>): boolean {
  const taskId = outreach.sourceRecipientTaskId;
  if (!taskId) return true;
  return !knownTaskIds.has(taskId);
}

/** Newest activity on the thread, for ordering against tasks (which sort on `updatedDate`). */
export function outreachUpdatedMs(outreach: OutreachRowSource): number {
  const candidates: (string | Date | undefined)[] = [outreach.updatedDate, lastMessage(outreach)?.createdDate, outreach.createdDate];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const ms = candidate instanceof Date ? candidate.getTime() : Date.parse(candidate);
    if (!Number.isNaN(ms)) return ms;
  }
  return 0;
}

/**
 * Who sent this outreach, for the row's title.
 *
 * The entity has no sender-name field, so this reads `metadata.senderDisplayName` when the assistant
 * resolved one, and otherwise lifts the name out of the `"Message from <name>: "` prefix the service
 * stamps on the opening message. Returns undefined when neither is available — the caller decides what
 * to show rather than this inventing a placeholder.
 */
export function senderNameOf(outreach: OutreachRowSource & { metadata?: { senderDisplayName?: string } }): string | undefined {
  const declared = outreach.metadata?.senderDisplayName?.trim();
  if (declared) return declared;
  const first = (outreach.messages ?? [])[0]?.message ?? '';
  const match = /^\s*Message from ([^:]{1,80}):/.exec(first);
  return match?.[1]?.trim() || undefined;
}

/**
 * The status to file an outreach row under.
 *
 * Deliberately reuses the AgentTask statuses rather than introducing an outreach-specific vocabulary.
 * `bucketContaining` is a pure switch on the status string and is mirrored 1:1 on native; every
 * consumer downstream of it — the tab counts, the status badge, `isSettled`, the island — reads that
 * one function. Mapping onto statuses it already understands means an outreach row sorts, buckets,
 * badges and filters exactly like a task with no change to any of them, instead of a `kind` branch
 * threaded through a dozen call sites.
 */
export function outreachRowStatus(outreach: OutreachRowSource, viewer: OutreachViewer): 'ACTION_NEEDED' | 'IN_PROGRESS' | 'COMPLETED' {
  const bucket = bucketOfOutreach(outreach, viewer);
  if (bucket === 'done') return 'COMPLETED';
  return bucket === 'waiting' ? 'ACTION_NEEDED' : 'IN_PROGRESS';
}

/**
 * What a task should say about an outreach it SENT.
 *
 * A task that reached out to somebody currently ends at "Done" and says nothing about what came of it
 * — the `sourceRecipientTaskId` link has existed all along and was read only to HIDE a row. This is the
 * shape that makes it tell you: who it went to, over what, and whether they ever answered.
 */
export type SentOutreachSummary = {
  id: string;
  /** Who it went to, when it could be resolved. */
  recipientName?: string;
  /** APP / WHATSAPP / SMS / EMAIL / TELEGRAM — the channel it actually went out on. */
  channel?: string;
  /** Did the other side ever write back? The single most useful fact about a sent outreach. */
  replied: boolean;
  /** Is it still open and waiting on THEM? */
  awaitingReply: boolean;
  /**
   * The last thing said on the thread, whoever said it.
   *
   * ⚠️ NO SURFACE RENDERS THIS ANY MORE — `messages` replaced it everywhere, because one last line was
   * the reported bug: on a thread that got a reply it is THEIRS, so the message the user sent in their
   * own name was the one thing not on screen. It is kept because it is the summary's only single-line
   * form and this type is mirrored 1:1 by both native ports, so dropping it is a three-surface change
   * with nothing to gain; it is flagged here so nobody reads its presence as a suggestion to use it.
   * **Anything that wants to show "what came of this" wants `messages`.**
   */
  lastMessage?: string;
  /**
   * THE CORRESPONDENCE — every message on the thread, oldest first, each attributed and dated.
   *
   * This is the field the task detail actually reads, and it exists because `lastMessage` alone was not
   * an answer to the question people ask of a task that messaged someone on their behalf: *what did it
   * say?* Reported verbatim — "I don't see the messages that the agent sent to each" — of a goal whose
   * tasks had each sent a real WhatsApp message. One truncated last line cannot show a two-turn exchange,
   * and on a thread that got a reply the last line is THEIRS, so the thing the user sent was the one
   * thing not on screen.
   *
   * Empty for an outreach the service holds no messages for. That is a real state and the surfaces say
   * so, rather than rendering an empty bubble list.
   */
  messages: SentThreadEntry[];
  /** The conversation to open to read it. */
  conversationId?: string;
};

/**
 * One message of a SENT correspondence, as the task detail renders it.
 *
 * `mine` is the USER's side (their agent sent it), so it takes the same end-aligned bubble the inbound
 * thread view gives the user's own replies — who spoke reads before a single word does.
 */
export type SentThreadEntry = {
  text: string;
  /** ISO, when the service recorded one. Absent on messages written before per-message dates existed. */
  at?: string;
  /** "2d ago" — computed once per response so two messages a second apart cannot disagree. */
  relative?: string;
  mine: boolean;
};

/**
 * WHICH TASK a sent outreach belongs to — the link the whole "what did this task say to whom" surface
 * hangs off, and the one that quietly broke.
 *
 * Two answers, in order:
 *
 *  1. `sourceRecipientTaskId`, the field this was originally keyed on. It is still the right answer for
 *     every row written before 2026-08-31 and is still READ off the entity — but nothing writes it any
 *     more. `ManageOutreaches.create` stopped stamping it in #9151 ("send from the conversation, without
 *     opening a task first"), which was correct for what that change was for: an outreach no longer
 *     REQUIRES a task, so a create must not have to name one.
 *  2. `sourceConversationId` — which #9151 stamps instead (`this.ctx.conversationId`) — matched against
 *     the task's own `agentConversationId`. For a worker run those are the same conversation, so this is
 *     the link, not a heuristic standing in for one.
 *
 * Keying on (1) alone is why a task that had really sent WhatsApp messages showed none: from the day
 * #9151 shipped, every new outreach answered "no task", the fold matched nothing, and the section that
 * exists to show this rendered null. Nothing failed and nothing logged — an unset field simply stopped
 * matching. Keep BOTH: (1) covers the rows already in the store, (2) covers everything since, and
 * neither needs a backfill.
 *
 * A conversation with no task behind it (a Maestro chat the user sent from) resolves to nothing, which
 * is correct — that outreach is not a task's work and has no task detail to appear on.
 *
 * Why (2) is a LINK and not a guess: every path that puts an agent on a task writes that agent's own
 * conversation to `task.agentConversationId` in the same patch as the status
 * (`dispatch-task-agent.ts`, `spawnSubAgent`'s write-back, `ensureTaskAgentRunning`), and
 * `ManageOutreaches` stamps `this.ctx.conversationId` — the conversation the tool is running in. For a
 * worker run those are the same id by construction.
 *
 * ⚠️ Documented limit: the index is keyed on `agentConversationId` only. An outreach sent from a task's
 * REVIEWER conversation resolves to no task. Left out deliberately rather than guessed at — the natives
 * mirror this function 1:1, and a second key none of the three can point at a real occurrence of is a
 * broader match maintained on speculation. If a reviewer-sent outreach ever turns up, add
 * `reviewerConversationId` to the index on all three surfaces at once.
 */
export function sentOutreachTaskId(
  outreach: OutreachRowSource,
  /** Each task's own agent conversation → that task's id. */
  taskIdByAgentConversation: ReadonlyMap<string, string>,
): string | undefined {
  if (outreach.sourceRecipientTaskId) return outreach.sourceRecipientTaskId;
  const conversationId = outreach.sourceConversationId;
  return conversationId ? taskIdByAgentConversation.get(conversationId) : undefined;
}

/**
 * Who an outreach was sent TO, for a sent-outreach summary. The entity carries no recipient name, so
 * this reads `metadata.recipientDisplayName` — the name the sender's own assistant resolved for them —
 * and gives up rather than guessing, since unlike the sender there is no prefix to fall back on.
 */
export function recipientNameOf(outreach: OutreachRowSource & { metadata?: { recipientDisplayName?: string } }): string | undefined {
  return outreach.metadata?.recipientDisplayName?.trim() || undefined;
}

/**
 * Did the other side ever reply to an outreach we sent?
 *
 * On a SENT outreach the viewer is the source, so `authorOfMessage` inverts: their messages are the
 * ones NOT from us. Any message after the first that is theirs counts — the first is our own opening
 * message by construction.
 */
export function summariseSentOutreach(
  outreach: OutreachRowSource & { metadata?: { recipientDisplayName?: string } },
  viewer: OutreachViewer,
  /** ONE instant for the whole response, so two messages a second apart cannot disagree about "just now". */
  now: Date = new Date(),
): SentOutreachSummary {
  const messages = outreach.messages ?? [];
  // On a sent outreach WE are the source, so a message is theirs when it is not ours. `direction`
  // answers it outright; the prefix fallback needs the inversion, since `"Reply from <them>"` on a
  // thread we sent is theirs, not ours.
  const isTheirs = (m: OutreachRowMessage): boolean => {
    if (m.direction === 'FROM_TARGET') return true;
    if (m.direction === 'FROM_SOURCE') return false;
    const text = (m.message ?? '').trimStart();
    if (!text.startsWith(REPLY_PREFIX)) return false; // our opening message and our own pushes
    const name = viewer.name?.trim();
    if (!name) return true; // a reply we cannot attribute to ourselves is theirs
    return !text.startsWith(`${REPLY_PREFIX}${name}:`);
  };
  const replied = messages.some(isTheirs);
  const last = messages[messages.length - 1];
  return {
    id: outreach.id,
    ...(recipientNameOf(outreach) ? { recipientName: recipientNameOf(outreach) } : {}),
    ...(outreach.preferredCommunicationChannel ? { channel: outreach.preferredCommunicationChannel } : {}),
    replied,
    awaitingReply: outreach.status !== 'COMPLETED' && !!last && !isTheirs(last),
    ...(last?.message ? { lastMessage: stripAuthorPrefix(last.message) } : {}),
    // The whole correspondence, attributed and dated. Built from `annotateThread` in SENT mode so `mine`
    // comes from the one place that knows the sides invert on a thread we raised — a second inversion
    // here is how the two would drift. The turn markers it also computes are deliberately dropped: on a
    // thread WE sent, "waiting on you" is a claim about the recipient, and this row is read by the sender.
    messages: annotateThread(messages, viewer, true).map((e) => ({
      text: e.text,
      ...(e.at ? { at: e.at } : {}),
      ...(relativeTime(e.at, now) ? { relative: relativeTime(e.at, now) } : {}),
      mine: e.mine,
    })),
    ...(outreach.conversationId || outreach.targetConversationId ? { conversationId: outreach.targetConversationId ?? outreach.conversationId } : {}),
  };
}

/**
 * Drop the `"Message from X: "` / `"Reply from X: "` prefix the service stamps into the message text.
 *
 * It is machine attribution, not something a person wrote, and the row already says who spoke — so
 * rendering it puts the same fact twice and reads as leaked plumbing. Shown live on the deployed list
 * as `"Reply from Ronny: רוני מציע…"`. The prefix is still what pre-`direction` rows are ATTRIBUTED by;
 * this only removes it from what gets displayed.
 */
export function stripAuthorPrefix(message: string): string {
  return message.replace(/^\s*(?:Message|Reply) from [^:]{1,80}:\s*/, '');
}

/** One line of the correspondence, as the thread view renders it. */
export type ThreadEntry = {
  text: string;
  /** ISO, when the service recorded one. Absent on messages written before per-message dates existed. */
  at?: string;
  mine: boolean;
  /**
   * This message ended the other side's turn and handed it to the user — an ask that needed an answer.
   * Derived from the trail itself rather than from any stored flag: the moment where the agent asked
   * the user to respond lives in the USER's own conversation, never on the outreach, so the only honest
   * source is who spoke and who spoke next.
   */
  awaitedYou: boolean;
  /** The user answered it, and this is the entry index that did. Absent while it is still unanswered. */
  resolvedByIndex?: number;
};

/**
 * Turn the flat `messages[]` into a correspondence: who said what, and — crucially — where the thread
 * was WAITING ON THE USER and whether that ever got resolved.
 *
 * `awaitedYou` marks a message of theirs that the user's own reply eventually answered, or that is
 * still hanging at the end of the thread. Consecutive messages from the same side collapse into one
 * turn, so a sender who wrote three lines in a row raises one ask, not three.
 */
export function annotateThread(
  messages: OutreachRowMessage[],
  viewer: OutreachViewer,
  sent = false,
  /**
   * The receiving agent judged this thread settled (the outreach is COMPLETED): what arrived asks nothing
   * of the user. It read the message; this function only reads the shape of the trail, so where the two
   * disagree the judgement wins.
   *
   * Without it "they spoke last" is the only available proxy for "they asked you something", and the two
   * part company on the commonest reply of all: an answer to a question the USER asked. Reported from a
   * real thread where the sender's "going well, releasing soon" closed the exchange and was badged
   * "Waiting on you" for it, so the more promptly someone answers, the more phantom work the user collects.
   * Structural marking stays the fallback for every thread no agent has judged.
   */
  settled = false,
): ThreadEntry[] {
  const isMine = (m: OutreachRowMessage): boolean => {
    if (!sent) return authorOfMessage(m, viewer) === 'me';
    // On a thread WE sent the sides invert: their messages are the ones that are not ours.
    if (m.direction === 'FROM_SOURCE') return true;
    if (m.direction === 'FROM_TARGET') return false;
    const text = (m.message ?? '').trimStart();
    if (!text.startsWith(REPLY_PREFIX)) return true;
    const name = viewer.name?.trim();
    if (!name) return false;
    return text.startsWith(`${REPLY_PREFIX}${name}:`);
  };

  const entries: ThreadEntry[] = messages.map((m) => ({
    text: stripAuthorPrefix(m.message ?? ''),
    ...(m.createdDate ? { at: m.createdDate instanceof Date ? m.createdDate.toISOString() : String(m.createdDate) } : {}),
    mine: isMine(m),
    awaitedYou: false,
  }));

  // The LAST message of each of their turns is the one that asked; the next message of ours resolves it.
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].mine) continue;
    const endsTheirTurn = i === entries.length - 1 || entries[i + 1].mine;
    if (!endsTheirTurn) continue;
    entries[i].awaitedYou = true;
    const answer = entries.findIndex((e, j) => j > i && e.mine);
    if (answer !== -1) entries[i].resolvedByIndex = answer;
  }
  // An entry the user already answered keeps its marker: "You answered" is a true record of what happened.
  // Only the trailing, unanswered ask is a claim about what the user still owes, and that is the claim the
  // agent's judgement overrides.
  if (settled) {
    for (const entry of entries) {
      if (entry.resolvedByIndex === undefined) entry.awaitedYou = false;
    }
  }
  return entries;
}

/**
 * A short relative time — "just now", "10m ago", "2d ago". The exact instant goes in a tooltip/tap
 * target alongside, because relative is what you scan and absolute is what you check.
 *
 * ⚠️ `createdDate` on an outreach message is documented as WALL-CLOCK time in the outreach SITE's
 * timezone, not an instant in the viewer's. Across a timezone gap the relative figure can therefore be
 * off by that offset. It is right for the common case (both parties in one timezone) and never claims
 * more precision than "2d ago" — but it is the reason this must not be used for anything that has to be
 * exact, and the reason the absolute value is always one interaction away.
 */
export function relativeTime(iso: string | undefined, now: Date): string | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return undefined;
  const diff = Math.max(0, now.getTime() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (day < 30) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}
